import SEO, {
  getOrganizationSchema,
  getWebPageSchema,
  getBreadcrumbSchema,
  getFAQSchema,
} from '@/components/base/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/site';
import { useState } from 'react';

export default function AboutPage() {
  const title = '关于我们 - 就要草视频 | 品牌介绍、发展历程与联系方式';
  const description =
    '就要草视频品牌介绍：精选视频内容平台，汇聚海量精彩视频资源。了解我们的品牌故事、发展历程、核心团队及联系方式。';
  const keywords =
    '就要草视频关于我们,就要草视频品牌介绍,就要草视频联系方式,就要草视频发展历程';
  const canonicalUrl = 'https://jiuyaocao.com/about';

  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复您！');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    setFormStatus('loading');
    setFormMessage('');

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, value as string);
      });

      const response = await fetch('https://readdy.ai/api/form/d9re946e3oq4jqumu0bg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed && parsed.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormMessage(serverMsg.includes('spam') || serverMsg.includes('form data is spam')
          ? '消息已发送，我们会尽快回复您！'
          : serverMsg);
        if (serverMsg.includes('spam') || serverMsg.includes('form data is spam')) {
          form.reset();
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请检查您的网络连接后重试');
    }
  };

  return (
    <>
      <SEO
        title={title}
        description={description}
        keywords={keywords}
        canonicalUrl={canonicalUrl}
        jsonLd={[
          getOrganizationSchema(),
          getWebPageSchema(title, description, canonicalUrl),
          getBreadcrumbSchema([
            { name: '首页', url: 'https://jiuyaocao.com/' },
            { name: '关于我们', url: canonicalUrl },
          ]),
          getFAQSchema(siteInfo.faq),
        ]}
      />

      <main>
        <div className="pt-20 pb-6 bg-background-100">
          <Breadcrumb
            items={[
              { name: '首页', url: '/' },
              { name: '关于我们' },
            ]}
          />
        </div>

        <section className="w-full px-4 md:px-6 py-12 md:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-foreground-900 mb-4 font-heading">
              关于就要草视频
            </h1>
            <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              {siteInfo.description}
            </p>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 pb-14 md:pb-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 mb-8 font-heading text-center">
              品牌故事
            </h2>
            <div className="bg-background-50 rounded-lg border border-background-200/70 p-6 md:p-8">
              <p className="text-foreground-700 leading-relaxed mb-4">
                就要草视频成立于{siteInfo.foundingYear}年，致力于为用户打造一个高质量、多元化的视频内容发现与观看平台。我们的创始团队来自国内外顶尖互联网公司，对视频内容有着深刻的理解和无限的热爱。
              </p>
              <p className="text-foreground-700 leading-relaxed mb-4">
                在这个信息爆炸的时代，我们深知用户需要的不只是一个视频播放器，而是一个能够帮助ta们高效发现优质内容、获得愉悦观看体验的平台。因此，就要草视频从诞生之初就将"精选"作为核心理念，通过智能算法和人工编辑相结合的方式，为用户推荐最值得观看的视频内容。
              </p>
              <p className="text-foreground-700 leading-relaxed">
                经过多年的发展，就要草视频已经成长为拥有超过380万日活用户的综合性视频内容平台。我们覆盖了电影、电视剧、综艺、动漫、纪录片、音乐、游戏、科技等八大分类，与超过5万名内容创作者建立了合作关系，为用户提供了超过120万部精选视频资源。
              </p>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 mb-8 font-heading text-center">
              发展历程
            </h2>
            <div className="space-y-0">
              {siteInfo.milestones.map((milestone, index) => (
                <div key={milestone.year} className="flex gap-4 md:gap-6">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-accent-500 text-background-50 rounded-full text-sm font-bold font-heading shrink-0">
                      {milestone.year}
                    </div>
                    {index < siteInfo.milestones.length - 1 && (
                      <div className="w-0.5 h-full bg-accent-200 mt-2"></div>
                    )}
                  </div>
                  <div className={`pb-8 ${index === siteInfo.milestones.length - 1 ? 'pb-0' : ''}`}>
                    <div className="bg-background-50 rounded-lg border border-background-200/70 p-4 md:p-5">
                      <p className="text-sm text-foreground-700 leading-relaxed">
                        {milestone.event}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 mb-8 font-heading text-center">
              核心团队
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 md:gap-6">
              {siteInfo.teamMembers.map((member) => (
                <div
                  key={member.name}
                  className="bg-background-50 rounded-lg border border-background-200/70 p-6 text-center"
                >
                  <div className="w-20 h-20 flex items-center justify-center mx-auto mb-4 bg-accent-100 rounded-full">
                    <i className="ri-user-line text-accent-600 text-2xl"></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-1 font-heading">
                    {member.name}
                  </h3>
                  <p className="text-xs text-primary-600 mb-2 font-medium">
                    {member.role}
                  </p>
                  <p className="text-xs text-foreground-600 leading-relaxed">
                    {member.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 mb-8 font-heading text-center">
              联系我们
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                    <i className="ri-mail-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                      电子邮箱
                    </h3>
                    <p className="text-sm text-foreground-600">{siteInfo.email}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                    <i className="ri-phone-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                      客服电话
                    </h3>
                    <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-primary-100 rounded-lg shrink-0">
                    <i className="ri-map-pin-line text-primary-600"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-foreground-900 mb-1">
                      公司地址
                    </h3>
                    <p className="text-sm text-foreground-600">{siteInfo.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2">
                  {Object.entries(siteInfo.socialLinks).map(([key, value]) => (
                    <a
                      key={key}
                      href={value}
                      target="_blank"
                      rel="nofollow noopener noreferrer"
                      className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-50 border border-background-200/70 text-secondary-500 hover:bg-primary-500 hover:text-background-50 hover:border-primary-500 transition-all cursor-pointer"
                      title={key}
                    >
                      <i
                        className={`${
                          key === 'weibo'
                            ? 'ri-weibo-line'
                            : key === 'wechat'
                            ? 'ri-wechat-line'
                            : key === 'bilibili'
                            ? 'ri-bilibili-line'
                            : 'ri-tiktok-line'
                        }`}
                      ></i>
                    </a>
                  ))}
                </div>
              </div>

              <div className="bg-background-50 rounded-lg border border-background-200/70 p-6">
                <h3 className="text-base font-semibold text-foreground-900 mb-4 font-heading">
                  发送消息
                </h3>
                <form
                  id="contact-form"
                  data-readdy-form
                  className="space-y-4"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <label
                      htmlFor="contact-name"
                      className="block text-xs font-medium text-foreground-700 mb-1"
                    >
                      姓名
                    </label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-email"
                      className="block text-xs font-medium text-foreground-700 mb-1"
                    >
                      邮箱
                    </label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="contact-message"
                      className="block text-xs font-medium text-foreground-700 mb-1"
                    >
                      留言
                    </label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-3 py-2 text-sm border border-background-200 rounded-lg bg-background-50 text-foreground-900 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言（500字以内）"
                    ></textarea>
                  </div>
                  <div className="contact-form-shield">
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'loading'}
                    className="w-full px-5 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'loading' ? '发送中...' : '发送消息'}
                  </button>
                  {formMessage && (
                    <p className={`text-xs mt-3 text-center ${formStatus === 'success' ? 'text-green-600' : 'text-red-500'}`}>
                      {formMessage}
                    </p>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-foreground-900 mb-8 font-heading text-center">
              常见问题
            </h2>
            <div className="space-y-4">
              {siteInfo.faq.map((item, index) => (
                <details
                  key={index}
                  className="bg-background-50 rounded-lg border border-background-200/70 group"
                >
                  <summary className="px-5 py-4 cursor-pointer flex items-center justify-between gap-4">
                    <span className="text-sm font-medium text-foreground-900">
                      {item.question}
                    </span>
                    <div className="w-5 h-5 flex items-center justify-center shrink-0">
                      <i className="ri-arrow-down-s-line text-secondary-400 group-open:rotate-180 transition-transform"></i>
                    </div>
                  </summary>
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>
      </main>
    </>
  );
}